import { useState, useMemo, useEffect } from "react";
import { useIntl } from "react-intl";

import { BranchesInfoAccordionContent } from "./BranchesInfoAccordionContent";
import { Accordion, Tabs } from "components";

import "./BranchesInfo.scss";

import {
  Enum_BranchesInfoFilter,
  Enum_BranchesInfoRegionFilters,
} from "./BranchesInfo.types";
import { BranchesInfoFilters } from "./BranchesInfoFilters";
import { BranchesInfoMap } from "./BranchesInfoMap";

import { useBranches } from "views/public/Branches/hooks";
import { generateTimeString } from "utils/times/generateTimeString";
import { BranchesInfoPinContent } from "./BranchesInfoPinContent";

export function BranchesInfo() {
  const { formatMessage } = useIntl();

  const [filterMode, setFilterMode] = useState<Enum_BranchesInfoFilter>(
    Enum_BranchesInfoFilter.clinic
  );

  const [mapPosition, setMapPosition] = useState({
    lat: 41.7151,
    lng: 44.8271,
  });

  const [activeBranchIndex, setActiveBranchIndex] = useState<number | undefined>();

  const [currentFilter, setCurrentFilter] = useState<Enum_BranchesInfoRegionFilters>(
    Enum_BranchesInfoRegionFilters.all
  );

  const { branches, isLoading, isFetching } = useBranches({
    placeType: filterMode,
    regionType: currentFilter,
  });

  useEffect(() => {
    setActiveBranchIndex(undefined);
  }, [currentFilter, filterMode]);

  const branchData = useMemo(() => {
    return branches?.map((branch: any, i: number) => {
      if (branch.latitude && branch.longitude) {
        return {
          content: (
            <BranchesInfoPinContent
              key={i}
              addressLabel={formatMessage({ id: "branches.map.pin.address" })}
              phoneLabel={formatMessage({ id: "branches.map.pin.phone" })}
              mailLabel={formatMessage({ id: "branches.map.pin.email" })}
              title={branch.title}
              email={branch.email}
              phone={branch.phone}
              address={branch.address}
            />
          ),
          coords: {
            lat: Number(branch.latitude),
            lng: Number(branch.longitude),
          },
        };
      }
      return null;
    });
  }, [branches, formatMessage]);

  return (
    <div className="branches__info">
      <div className="branches__info-info">
        <Tabs>
          <Tabs.TabsButton
            isActive={filterMode === Enum_BranchesInfoFilter.clinic}
            onClick={() => setFilterMode(Enum_BranchesInfoFilter.clinic)}
          >
            {formatMessage({ id: "branches.clinics", defaultMessage: "_branches.clinics_" })}
          </Tabs.TabsButton>
          <Tabs.TabsButton
            isActive={filterMode === Enum_BranchesInfoFilter.laboratory}
            onClick={() => setFilterMode(Enum_BranchesInfoFilter.laboratory)}
          >
            {formatMessage({ id: "branches.laboratories", defaultMessage: "_branches.laboratories_" })}
          </Tabs.TabsButton>
        </Tabs>

        <BranchesInfoFilters
          currentFilter={currentFilter}
          setCurrentFilter={setCurrentFilter}
        />

        <div className="branches__info-info-accordion">
          {(!isLoading || isFetching) &&
            branches?.map((branch: any, index: number) => (
              <Accordion
                key={index}
                active={index === activeBranchIndex}
                title={branch.title}
                onClick={() => {
                  const newIndex = index === activeBranchIndex ? undefined : index;
                  if (branch.latitude && branch.longitude) {
                    setMapPosition({ lat: Number(branch.latitude), lng: Number(branch.longitude) });
                  }
                  setActiveBranchIndex(newIndex);
                }}
              >
                <BranchesInfoAccordionContent
                  onClick={() => {
                    if (branch.latitude && branch.longitude) {
                      setMapPosition({ lat: Number(branch.latitude), lng: Number(branch.longitude) });
                      setActiveBranchIndex(index);
                    }
                  }}
                  title={branch.title}
                  coords={{
                    lat: Number(branch.latitude),
                    lng: Number(branch.longitude),
                  }}
                  address={branch.address}
                  phone={branch.phone}
                  email={branch.email}
                  workingHours={
                    branch?.working_time
                      ? generateTimeString(branch.working_time, formatMessage)
                      : ""
                  }
                />
              </Accordion>
            ))}
        </div>
      </div>

      <div className="branches__info-map">
        {(!isLoading || isFetching) && (
          <BranchesInfoMap
            customCenter={mapPosition}
            branchData={branchData}
            activeBranchIndex={activeBranchIndex}
          />
        )}
      </div>
    </div>
  );
}
